import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject, take } from 'rxjs';
import { OverlayTeardownService } from 'src/app/core/services/overlay-teardown.service';
import { PushNotificationService } from 'src/app/core/push/push-notification.service';
import { OfflineModeState } from 'src/app/core/offline/offline-mode.state';
import { selectAuth, State } from 'src/app/store/auth/auth.reducers';
import { BaseAction, SessionActionType } from 'src/app/store/store.actions';
import { environment } from 'src/environments/environment';
import { Auth, EMPTY_AUTH } from '../models/auth';
import { ENROLL_ROUTE } from '../utils/post-auth-target';
import { isAppPath, routePathOf } from '../utils/return-url';
import { AccessTokenHolder } from './access-token-holder.service';

export const AUTH_CHANNEL_NAME = 'galactus.auth';

/**
 * What this service SENDS. Every field is required — a producer that forgets one is a bug this type
 * must catch, so do NOT loosen it to accommodate the receiving side (see {@link InboundAuthSyncMessage}).
 *
 * `enrollmentRequired` rides along so the forced-2FA-enrollment flag — which lives only in the
 * in-memory store and is NOT rehydrated — stays consistent across tabs. Without it, a tab that
 * finishes enrollment (or is freshly forced into it) leaves sibling tabs with a stale flag, and
 * the enrollment guards wedge them on /auth/enroll (or wrongly admit them to /app).
 */
export type AuthSyncMessage =
  | { type: 'login'; auth: Auth; enrollmentRequired: boolean }
  | { type: 'logout' };

/**
 * What ARRIVES off the BroadcastChannel: the same shape, but untrusted. Every payload field is optional
 * because nothing enforces that the sender is a matching build of this app — an older tab, a partially
 * deployed version, or a hand-crafted message can omit any of them. Keeping the two types separate is
 * the point: the handler is forced to treat each field as possibly-absent (a payload with no `auth` is
 * ignored outright, rather than a runtime TypeError mid-handler), WITHOUT that leniency leaking back
 * onto `notifyLogin`, where a missing field really would be a caller bug.
 */
export type InboundAuthSyncMessage =
  | { type: 'login'; auth?: Auth; enrollmentRequired?: boolean }
  | { type: 'logout' };

/**
 * What a freshly applied identity says about WHO this tab now belongs to.
 *
 * <ul>
 *   <li>{@code 'same'} — provably the same user, or a tab with no identity to protect
 *       ({@code userId} 0). Nothing to rescue.
 *   <li>{@code 'switch'} — provably a DIFFERENT user: both userIds are known and they differ. Only
 *       this verdict earns the rescue ({@link AuthSyncService.closeOverlaysForIdentitySwitch} +
 *       {@link AuthSyncService.leaveAfterSwitch}; the broadcast path also drops the bearer).
 *   <li>{@code 'unproven'} — the identities cannot be compared. The identity is still applied and
 *       NOTHING else runs: every action on offer is destructive (a force-closed editor dialog loses
 *       what the user typed), and this is the ROUTINE outcome, not an exotic one — the dominant
 *       producer of identity applications is {@code AuthRefreshService.applyToken} on every silent
 *       refresh, and {@code decodeAuthToken} does not validate the {@code userId} claim.
 * </ul>
 *
 * <p>Full rationale — including why the close is not "the cheap, reversible half" — in CLAUDE.md,
 * "Cross-tab identity switch".
 */
export type IdentityChange = 'same' | 'switch' | 'unproven';

/**
 * Propagates login/logout across already-open tabs via BroadcastChannel.
 * One instance per tab (created at bootstrap by AppComponent). It only mutates
 * local auth state in response to OTHER tabs; navigation after a remote login
 * is left to the mounted /auth screen, which reacts to the auth state change.
 *
 * <p>The bearer token deliberately does NOT travel over the channel -- the {@link Auth}
 * payload is the non-secret identity slice only. On a cross-tab login, the receiving tab
 * has no access token in memory; its first protected REST call returns 401 and the HTTP
 * interceptor mints one via {@code /v2/refresh} using the shared httpOnly refresh cookie.
 */
@Injectable({ providedIn: 'root' })
export class AuthSyncService implements OnDestroy {
  private channel: BroadcastChannel | null;

  private readonly identitySwitchNavigated = new Subject<void>();

  /** One console line per tab for identity-less inbound logins — see where it is raised. */
  private warnedAboutPayloadlessLogin = false;

  /**
   * Fires when an identity switch has just yanked THIS tab off the page it was on (see
   * {@link leaveAfterSwitch}). Consumed by AppComponent, which shows the user-facing notice.
   *
   * <p>An event rather than a toast raised here on purpose: opening a toast means DialogService →
   * MatDialog/MatSnackBar, i.e. a hard Material dependency on a service that dozens of specs pull in
   * transitively (AuthService → AuthSyncService), and every one of those TestBeds would then need the
   * Material modules to construct — the same NullInjectorError trap OverlayTeardownService takes its
   * {@code @Optional()} deps to avoid.
   */
  readonly identitySwitchNavigated$: Observable<void> = this.identitySwitchNavigated.asObservable();

  constructor(
    private store: Store<State>,
    private router: Router,
    private tokenHolder: AccessTokenHolder,
    private overlayTeardown: OverlayTeardownService,
    private zone: NgZone,
    private push: PushNotificationService,
    private offlineMode: OfflineModeState,
  ) {
    this.channel = this.createChannel();
    if (this.channel) {
      // RE-ENTER THE ANGULAR ZONE. zone.js does not patch BroadcastChannel, so `onmessage` fires
      // OUTSIDE the zone and nothing this handler does schedules change detection: the dialog closes
      // would not repaint and the notice toast would render late or blank. Every visible consequence
      // lives inside handle(), so the whole call is wrapped. Same trap and fix as UserSettingStore's
      // zone.run on the 'user-settings' channel. Cheap — cross-tab auth messages are rare.
      this.channel.onmessage = (event: MessageEvent<InboundAuthSyncMessage>) =>
        this.zone.run(() => this.handle(event.data));
    }
  }

  // Close the channel on teardown so it (and its onmessage handler) don't outlive the service. Harmless
  // for the app-lifetime root singleton, but Karma re-instantiates the service per spec: an unclosed
  // channel per spec accumulates open 'galactus.auth' channels and a cross-tab test can receive echoes
  // from a prior spec's still-open channel. Mirrors DateFormatService.ngOnDestroy.
  ngOnDestroy(): void {
    if (this.channel) {
      this.channel.onmessage = null;
      this.channel.close();
      this.channel = null;
    }
    this.identitySwitchNavigated.complete();
  }

  /** Overridable in tests / environments without BroadcastChannel. */
  protected createChannel(): BroadcastChannel | null {
    return typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(AUTH_CHANNEL_NAME) : null;
  }

  notifyLogin(auth: Auth, enrollmentRequired = false): void {
    this.channel?.postMessage({ type: 'login', auth, enrollmentRequired });
  }

  notifyLogout(): void {
    this.channel?.postMessage({ type: 'logout' });
  }

  private handle(message: InboundAuthSyncMessage): void {
    if (message.type === 'login') {
      const incoming = message.auth;
      if (!incoming) {
        // UNINTERPRETABLE — ignore the message WHOLE: no dispatch, no overlay close, no teardown. A
        // `login` carrying no identity says only "somebody signed in somewhere"; there is nothing to
        // apply, and dispatching EMPTY_AUTH as "the safe reading" was worse than doing nothing (the
        // store would say logged-out while the PER-TAB bearer stayed live — full reasoning in
        // CLAUDE.md). A matching build cannot send this (`AuthSyncMessage` requires `auth`; only the
        // inbound type is lenient), so it means an older/partially-deployed tab or a hand-crafted
        // message: worth a console line, not silence. ONCE per tab, though — this is a deployment
        // tripwire, not a per-message log, and the dominant producer of `login` messages is
        // AuthRefreshService.applyToken on EVERY silent refresh, so one stale sibling tab would
        // otherwise warn here for the whole lifetime of this one.
        if (!this.warnedAboutPayloadlessLogin) {
          this.warnedAboutPayloadlessLogin = true;
          console.warn('[AuthSyncService] ignoring an inbound login message with no auth payload');
        }
        return;
      }
      // A cross-tab login can SWAP the identity under this tab: user B signs in elsewhere and, because
      // the refresh cookie is shared, this tab silently becomes B while A's screen — and any dialog
      // opened against A's record — is still in front of the user. isAuthenticated never drops on that
      // path, so the userId change is the only signal. Read BEFORE the dispatch below, which overwrites
      // it. See IdentityChange for why the verdict is three-way and why 'unproven' earns nothing.
      const identity = this.classifyIdentity(incoming);
      if (identity === 'switch') {
        this.closeOverlaysForIdentitySwitch();
        // DROP THE OUTGOING USER'S BEARER — this is what actually hands the tab over, and it is the one
        // step the refresh path must NOT copy (there the holder has just been handed the incoming
        // user's own valid token). The token lives in memory PER TAB, so a login that happened
        // elsewhere never touched this one: without the clear the store says B while every request
        // still goes out, authorized and recorded, as A. Cleared BEFORE the navigation, so the
        // destination's landing fetches don't go out on the outgoing user's bearer.
        this.tokenHolder.clear();
      }
      // Apply the identity and the enrollment flag ATOMICALLY (one action, both reducers — mirroring
      // AuthService.applyToken), or a sibling tab parked on /auth/login sees the new identity beside a
      // stale flag and burns its captured return URL (see "APPLY_SESSION is the single writer" in
      // CLAUDE.md). `!!` for the malformed-payload case (see InboundAuthSyncMessage): a missing flag
      // must read as "not required", never travel into the store as `undefined`, where it decides
      // whether the user is confined to the wizard. (`auth` needs no fallback — a payload without one
      // returned above, unapplied.)
      this.store.dispatch(new BaseAction(SessionActionType.APPLY_SESSION, { auth: incoming, enrollmentRequired: !!message.enrollmentRequired }));
      // When the cross-tab login is a FORCED enrollment, drive a sibling tab that is PARKED ON AN /auth
      // SCREEN to the wizard ourselves. The mounted /auth/login screen's isAuthenticated watcher
      // deliberately suppresses its own navigation while enrollmentRequired is true (it must not burn the
      // captured return URL), and redirectIfAuthedGuard is take(1)/non-reactive — so without this a tab
      // parked on /auth/login would be left authenticated-but-stranded on the login form with no route out.
      //
      // Scope the navigation to /auth tabs on purpose: a sibling tab already WORKING under /app (e.g. an
      // admin flipping require-2FA mid-session, so a refresh mints an enrollment token) must NOT be yanked
      // off its in-progress screen and lose unsaved input. That tab keeps the (now-true) flag from the
      // atomic APPLY_SESSION above and enrollmentRequiredGuard redirects it to the wizard on its NEXT
      // navigation — no data-losing interrupt.
      if (identity === 'switch') {
        // ONE entry point for every destination a switch can have — /app, the wizard, a parked /auth
        // screen — so this path and the refresh path (which calls the same method) cannot diverge on
        // which of them gets rescued.
        this.leaveAfterSwitch(!!message.enrollmentRequired);
        // The stored subscription still maps to the OUTGOING user, so their notifications would
        // land on the incoming user's device. Silent: browser permission is scoped to the origin,
        // not the user, so an already-granted device needs no gesture — and if permission is not
        // granted this does nothing and NO PROMPT APPEARS.
        //
        // Called AFTER tokenHolder.clear() on purpose, and it does NOT belong above it: a POST sent
        // before the clear would re-register this device under the OUTGOING user, which is the one
        // row this path exists to re-point. There is no bearer at this instant either, so
        // refreshForCurrentIdentity waits for the incoming one rather than POSTing without any —
        // /v2/push/devices is carved out of the interceptor's 401 refresh-retry, so an
        // unauthenticated attempt has nothing to rescue it. See the method.
        this.push.refreshForCurrentIdentity(!!message.enrollmentRequired);
      } else if (message.enrollmentRequired && routePathOf(this.router.url).startsWith('/auth')) {
        // Enrollment half of the shared post-auth rule (see resolvePostAuthTarget); the non-enrollment
        // case is handled by the mounted /auth screen's own watcher. (A switch takes the branch above,
        // which covers this same case for the incoming identity.)
        this.router.navigateByUrl(ENROLL_ROUTE);
      }
    } else {
      // Dismiss any dialog open in THIS tab. This branch tears the session down itself rather than
      // delegating to AuthService.clearSession, so it needs its own close: the CDK overlay container
      // hangs off <body>, outside the router-outlet, so the navigation below can't remove a dialog and
      // (being usually `disableClose`) the user would be stuck behind it over the login page. Blanket
      // apart from exemptFromTeardown (the one-time 2FA backup codes). The enrollment-wizard tab is
      // exempt from the NAVIGATION below for that same reason, but not from this close: its copy of
      // those codes is a route step, not a dialog, so the navigation is what would wipe them.
      this.overlayTeardown.closeAll();
      // Drop this device's push subscription, exactly as AuthService.clearSession does. This branch
      // tears the session down ITSELF rather than delegating, so anything clearSession does has to
      // be repeated here or it simply never happens in a tab that learns of the logout over the
      // channel. Without it a cross-tab logout left the subscription row alive for a user who has
      // signed out — on EVERY tab except the one where they clicked — which is the browser/backend
      // disagreement the teardown exists to prevent.
      //
      // MUST run BEFORE tokenHolder.clear(): the DELETE is subscribed synchronously, so the
      // interceptor stamps it with whatever bearer is in the holder at this instant, and the
      // backend identifies the row by the calling user. After the clear it would go out without a
      // bearer, 401, and — being carved out of 401 handling — fail silently, leaving the row.
      //
      // It also does the two things no OTHER tab can do on this one's behalf: it bumps the epoch,
      // so a subscribe in flight HERE is abandoned instead of re-registering the device, and it
      // drops this tab's cached endpoint and posted-endpoint mirror, which are per-tab in-memory
      // state the logging-out tab never touched.
      this.push.removeCurrentSubscription();
      this.tokenHolder.clear();
      // Do NOT clear RETURN_URL_KEY here. It is per-tab sessionStorage: this handler runs in a SIBLING
      // tab reacting to another tab's logout, and that tab may have JUST captured its own return URL
      // (e.g. its session expired on /app/orders/5, the interceptor captured it, and it is mid-relogin).
      // Wiping it would drop that tab's deep-link so the user lands on the home page after signing back
      // in. It is consumed (and cleared) on the next successful login and overwritten on the next
      // capture, so leaving it is safe.
      // Clear the identity AND the forced-2FA-enrollment flag in ONE atomic APPLY_SESSION (both reducers
      // key off it), mirroring AuthService.clearSession — no separate FULL + clear pair to order.
      this.store.dispatch(new BaseAction(SessionActionType.APPLY_SESSION, { auth: EMPTY_AUTH, enrollmentRequired: false }));
      // A logout elsewhere also ends this tab's offline mode, if it is in one -- BEFORE the navigation,
      // which the offline guard on /auth would otherwise bounce back to the catalogue. (The sender
      // already removed the shared identity and the storage listener would get here too, but not
      // necessarily first.)
      this.offlineMode.exit();
      // Do NOT yank an enrollment-wizard tab to /auth/login: it may be on the final step showing the
      // one-time backup codes (shown once, unrecoverable), and this SPA navigation would tear the wizard
      // down (ngOnDestroy wipes them). The wizard owns its own exit — its beforeunload guard warns on a
      // real unload, and once its store is cleared (above) its cross-tab escape subscription no-ops
      // (unauthenticated + step===3/!saved), so it simply stays put until the user leaves deliberately.
      // Every other parked tab still routes to login. (EnrollComponent's own step===3/!saved skip guards
      // the ENROLLMENT-flag subscription, NOT this logout navigation, so that guard doesn't cover it.)
      if (!this.router.url.startsWith(ENROLL_ROUTE)) {
        this.router.navigateByUrl('/auth/login');
      }
    }
  }

  /**
   * <b>Step 1 of the identity-switch rescue</b> — dismiss every overlay this tab has open, including
   * the ones exempt from an ordinary teardown.
   *
   * <p>{@code 'identity-switch'}, NOT the default {@code 'session-teardown'}: the tab has been handed
   * to a DIFFERENT user, so even the teardown-exempt dialogs go. The one exemption in the tree is the
   * one-time 2FA backup codes, and leaving those pinned over the incoming user's session would show
   * one user's recovery secrets to another (see {@code OverlayCloseReason}).
   *
   * <p>Public because the rescue has TWO callers: {@link handle}, for a switch learnt off the
   * BroadcastChannel, and {@code AuthRefreshService.applyToken}, for a switch this tab learns
   * FIRST-HAND when its own silent refresh comes back as another user. BroadcastChannel does not echo
   * to the sender, so the refreshing tab never receives its own {@code login} message — without this
   * it would be the ONE tab left holding the outgoing user's dialogs and page. Call it BEFORE the
   * {@code APPLY_SESSION} dispatch, pairing with {@link leaveAfterSwitch} after it.
   */
  closeOverlaysForIdentitySwitch(): void {
    this.overlayTeardown.closeAll('identity-switch');
  }

  /**
   * <b>Step 2 of the identity-switch rescue</b> — navigate this tab off whatever it was showing for
   * the OUTGOING user, and announce it when there was something to lose.
   *
   * <p><b>Every destination lives here, in ONE method, because the rescue has two callers</b>
   * ({@link handle} and {@code AuthRefreshService.applyToken} — see
   * {@link closeOverlaysForIdentitySwitch}) and a per-caller branch is how one of them silently ends
   * up covering fewer screens than the other. Three cases:
   *
   * <ul>
   *   <li><b>Under {@code /app}</b> — closing the dialog was only half the rescue: the PAGE behind it
   *       still renders the outgoing user's record, so an ordinary (non-dialog) form on that screen
   *       would save their data under the incoming user. Navigating destroys the routed component,
   *       which is the only thing that reliably drops that in-memory view state (why not a reload, in
   *       CLAUDE.md). {@code enrollmentRequired} picks the target: an identity confined to the 2FA
   *       wizard would be bounced there off home by {@code enrollmentRequiredGuard} anyway, so skip
   *       the home hop. Announced — the user's screen was replaced out from under them.
   *   <li><b>On the enrollment wizard</b> — {@link leaveWizardAfterSwitch}, the one {@code /auth}
   *       screen a switch must not leave alone.
   *   <li><b>Any other {@code /auth} screen</b> — nothing of the outgoing user's is on display, so no
   *       navigation and no notice; the mounted screen owns its own routing. The one exception is a
   *       wizard-scoped incoming identity, which the parked tab cannot route itself to (the login
   *       screen's watcher deliberately suppresses that navigation) — same rule as the non-switch
   *       enrollment branch in {@link handle}.
   * </ul>
   *
   * <p>Go through {@code routePathOf}, never the raw {@code Router.url} — it carries the
   * query/fragment, and {@code isAppPath} matches the bare {@code /app} by equality, so an unstripped
   * {@code /app?tab=1} would silently skip the rescue.
   *
   * <p>Call this AFTER the {@code APPLY_SESSION} dispatch, so guards and any landing fetch resolve
   * under the new identity. The notice fires after {@code navigateByUrl} as an ordering convention
   * only — that call is async, and the toast survives either way.
   */
  leaveAfterSwitch(enrollmentRequired: boolean): void {
    const path = routePathOf(this.router.url);
    if (path.startsWith(ENROLL_ROUTE)) {
      this.leaveWizardAfterSwitch();
      return;
    }
    if (isAppPath(path)) {
      this.router.navigateByUrl(enrollmentRequired ? ENROLL_ROUTE : environment.homePage);
      // TELL THE USER: everything about the rescue is invisible from this tab — the dialog vanishes,
      // the screen is replaced, unsaved input is gone — and the cause was a login somewhere else. An
      // unexplained swap reads as the very crash the navigation was chosen over a reload to avoid.
      this.identitySwitchNavigated.next();
      return;
    }
    if (enrollmentRequired && path.startsWith('/auth')) {
      this.router.navigateByUrl(ENROLL_ROUTE);
    }
  }

  /**
   * Drop the enrollment wizard after this tab has been handed to a different user.
   *
   * <p><b>Aim at {@code /auth/login}, never at the incoming user's real destination.</b> That route
   * carries {@code redirectIfAuthedGuard}, which applies the shared post-auth rule
   * ({@code resolvePostAuthTarget}) for whoever the tab now belongs to — the single authority on
   * where a freshly applied identity goes, so this must not second-guess it.
   *
   * <p>No notice: unlike {@link leaveAfterSwitch} this tab was on an /auth screen, where there is no
   * record on display and no unsaved form to mourn — the codes it showed belong to a session that
   * has ended.
   *
   * <p><b>Known limitation — an incoming identity that is ITSELF wizard-scoped keeps the mounted
   * step.</b> The guard cancels the {@code /auth/login} activation and redirects to
   * {@code ENROLL_ROUTE}, which is the URL the tab is already on, and the router ignores a
   * same-URL navigation — a cancelled navigation destroys nothing, so the outgoing user's codes
   * survive. Every alternative destination is bounced straight back by
   * {@code enrollmentRequiredGuard}, so there is no route to leave through; clearing the step would
   * have to be {@code EnrollComponent}'s own reaction to the identity change. Left as is: it needs
   * BOTH users to be unenrolled forced-2FA accounts signing in on the same machine at the same
   * time, while the common case (the incoming user is an ordinary one) is routed away correctly.
   */
  private leaveWizardAfterSwitch(): void {
    this.router.navigateByUrl('/auth/login');
  }

  /** Who a freshly applied identity says this tab now belongs to — see {@link IdentityChange} for
   *  what each verdict earns. The store's auth slice is a BehaviorSubject-backed selector, so take(1)
   *  resolves synchronously and the value is readable as a plain return; the read completes before the
   *  caller dispatches the new identity over it, so it MUST be called first.
   *
   *  <p>Only a comparison of two KNOWN userIds can return {@code 'switch'}; anything unreadable is
   *  {@code 'unproven'} ({@code incoming} comes off a payload whose {@code userId} nothing validates).
   *  The store read is guarded on {@code resolved}, not on {@code current} being set — that branch is
   *  unreachable while NgRx emits synchronously, but if it ever went async, reading the miss as "same
   *  user, nothing to do" would fail OPEN on every login.
   *
   *  <p>A genuinely EMPTY identity ({@code userId} 0 — a tab parked on /auth/login) is NOT unproven:
   *  it is a resolved read of "nothing to protect", so it is {@code 'same'}. */
  classifyIdentity(incoming: Auth | null | undefined): IdentityChange {
    let current: Auth | undefined;
    let resolved = false;
    // Disposed explicitly rather than left to `take(1)`: take(1) only unsubscribes once a value
    // arrives, so on the unresolved path below (a source that never emits) the subscription would
    // stay live — one leak per cross-tab login, on the very branch written to survive that source
    // ever going async.
    const sub = this.store
      .select(selectAuth)
      .pipe(take(1))
      .subscribe(a => { current = a; resolved = true; });
    sub.unsubscribe();
    if (!resolved) {
      return 'unproven';
    }
    if (!current?.userId) {
      return 'same';
    }
    if (!incoming?.userId) {
      return 'unproven';
    }
    return current.userId === incoming.userId ? 'same' : 'switch';
  }
}
